import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Compass, ChartLineUp, FileText, Heartbeat, Gauge, SignOut, Lock, MusicNotes, Ticket } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../api";

export const RANGES = [
  ["today", "Today"], ["yesterday", "Yesterday"], ["last_7_days", "Last 7 days"],
  ["last_30_days", "Last 30 days"], ["current_season", "Current season"],
  ["previous_season", "Previous season"], ["all_time", "All time"],
];

const STATUS_LABEL = {
  insufficient_data: "Insufficient data", unavailable: "Unavailable",
  not_configured: "Not configured",
};

export function MetricValue({ metric, suffix = "" }) {
  if (metric == null) return <span className="text-ff-muted">—</span>;
  if (typeof metric !== "object") return <span>{metric}{suffix}</span>;
  const st = metric.status;
  if (st && st !== "ok") {
    return <span className="text-xs italic text-ff-muted" data-testid="metric-status">{STATUS_LABEL[st] || "—"}</span>;
  }
  const v = metric.value;
  if (v == null) return <span className="text-ff-muted">—</span>;
  return <span>{typeof v === "number" ? v.toLocaleString() : v}{suffix}</span>;
}

export function MetricCard({ label, metric, suffix = "", testid, hint }) {
  return (
    <div className="rounded-md border border-ff-brass/20 bg-ff-bg2/60 p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest text-ff-brass">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold text-ff-gold">
        <MetricValue metric={metric} suffix={suffix} />
      </div>
      {hint && <div className="mt-0.5 text-[11px] text-ff-muted">{hint}</div>}
    </div>
  );
}

export function StatusPill({ state }) {
  const map = {
    healthy: "border-emerald-500/40 text-emerald-300", degraded: "border-ff-gold/50 text-ff-gold",
    critical: "border-red-500/50 text-red-300", unknown: "border-ff-brass/30 text-ff-muted",
    not_configured: "border-ff-brass/30 text-ff-muted",
  };
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider ${map[state] || map.unknown}`}>{state}</span>;
}

export function RangeBar({ range, setRange, tz, setTz, meta }) {
  return (
    <div className="ff-panel flex flex-wrap items-center justify-between gap-3 rounded-md p-3" data-testid="founder-rangebar">
      <div className="flex flex-wrap gap-1.5">
        {RANGES.map(([k, lbl]) => (
          <button key={k} onClick={() => setRange(k)} data-testid={`range-${k}`}
            className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${range === k ? "border-ff-gold bg-ff-gold/10 text-ff-gold" : "border-ff-brass/25 text-ff-muted hover:text-ff-parchment"}`}>{lbl}</button>
        ))}
      </div>
      <div className="flex items-center gap-2 text-[11px] text-ff-muted">
        <button onClick={() => setTz(tz === "UTC" ? "America/New_York" : "UTC")} data-testid="tz-toggle"
          className="rounded-full border border-ff-brass/25 px-2.5 py-1 text-ff-brass hover:text-ff-gold">TZ: {tz}</button>
        {meta?.label && <span data-testid="range-label">{meta.label}</span>}
        {meta?.generated_at && <span>· updated {String(meta.generated_at).slice(11, 19)} UTC</span>}
      </div>
    </div>
  );
}

const NAV = [
  ["/founder", "Overview", Gauge],
  ["/founder/analytics", "Analytics", ChartLineUp],
  ["/founder/reports", "Reports", FileText],
  ["/founder/system-health", "System", Heartbeat],
  ["/founder/audio", "Audio", MusicNotes],
  ["/founder/beta", "Beta", Ticket],
];

export function FounderShell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-ff-bg text-ff-parchment">
      <header className="sticky top-0 z-40 border-b border-ff-brass/25 bg-ff-bg/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <Compass size={26} weight="duotone" className="text-ff-gold animate-compass" />
            <div>
              <div className="font-display text-lg font-bold text-ff-gold">Founder Command Center</div>
              <div className="text-[10px] uppercase tracking-widest text-ff-brass">Aurumward · Confidential</div>
            </div>
          </div>
          <button data-testid="founder-logout" onClick={() => { logout(); nav("/"); }}
            className="flex items-center gap-1.5 rounded-sm border border-ff-brass/30 px-3 py-1.5 text-sm text-ff-muted hover:border-ff-gold hover:text-ff-gold">
            <SignOut size={16} /> Exit
          </button>
        </div>
        <nav className="mx-auto flex max-w-6xl items-center gap-1 overflow-x-auto px-4 pb-2">
          {NAV.map(([to, label, Icon]) => (
            <Link key={to} to={to} data-testid={`fnav-${label.toLowerCase()}`}
              className={`flex items-center gap-1.5 whitespace-nowrap rounded-sm px-3 py-1.5 text-sm transition-colors ${loc.pathname === to ? "bg-ff-bg2 text-ff-gold" : "text-ff-muted hover:text-ff-parchment"}`}>
              <Icon size={15} weight="duotone" /> {label}
            </Link>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6">{children}</main>
    </div>
  );
}

// Step-up reauth modal hook: returns [ensure, modalElement]
export function useReauth() {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [resolver, setResolver] = useState(null);

  const ensure = () => new Promise((resolve) => { setResolver(() => resolve); setOpen(true); });

  const submit = async () => {
    try {
      await api.post("/founder/reauth", { password: pw });
      toast.success("Re-authenticated");
      setOpen(false); setPw("");
      resolver && resolver(true);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const modal = open ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="reauth-modal">
      <div className="ff-panel w-full max-w-sm rounded-md p-5">
        <div className="mb-2 flex items-center gap-2 text-ff-gold"><Lock size={18} weight="duotone" /><h3 className="font-display text-lg font-semibold">Confirm your password</h3></div>
        <p className="mb-3 text-xs text-ff-muted">Sensitive exports require recent authentication.</p>
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reauth-password"
          className="w-full rounded-sm border border-ff-brass/30 bg-ff-bg p-2.5 text-sm text-ff-parchment" placeholder="Password" />
        <div className="mt-3 flex justify-end gap-2">
          <button onClick={() => { setOpen(false); setPw(""); }} className="rounded-sm border border-ff-brass/30 px-3 py-1.5 text-sm text-ff-muted">Cancel</button>
          <button onClick={submit} data-testid="reauth-submit" className="rounded-sm bg-ff-gold px-3 py-1.5 text-sm font-semibold text-ff-bg hover:bg-ff-brass">Confirm</button>
        </div>
      </div>
    </div>
  ) : null;

  return [ensure, modal];
}

export function DistBars({ dist, testid }) {
  const entries = Object.entries(dist || {});
  const max = Math.max(1, ...entries.map(([, v]) => v));
  if (entries.length === 0) return <p className="text-sm text-ff-muted">No data yet.</p>;
  return (
    <div className="space-y-1.5" data-testid={testid}>
      {entries.map(([k, v]) => (
        <div key={k} className="text-sm">
          <div className="flex justify-between text-ff-muted"><span className="capitalize">{k.replace(/_/g, " ")}</span><span className="text-ff-parchment">{v}</span></div>
          <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-ff-bg"><div className="h-full bg-ff-brass" style={{ width: `${(v / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}
